<template>
  <v-card>
    <abertura-manual-ticket-dialog
      v-if="filaId"
      :fila-id="filaId"
      :is-info-dialog-open.sync="isAddNewTicketDialogOpen"
      @refetch-data="getTicketsAndSelectFirst"
    />

    <ticket-filtros-dialog
      :fila-id="filaId"
      :is-filtros-dialog-open.sync="isFiltrosDialogOpen"
      :is-gestor="isGestor"
      :filtros-foram-alterados="filtrosForamAlterados"
      :filtrar-tickets="filtrarTickets"
      @update:filtro-email="v => filtroEmailLocale = v"
      @update:filtro-protocolo="v => filtroProtocoloLocale = v"
      @update:filtro-agente-selecionado="v => filtroAgenteSelecionadoLocale = v"
    />

    <v-row class="ma-0 pl-2">
      <categoria-status-chip-groups
        :fila-id="filaId"
        :is-gestor="isGestor"
        :filtro-categoria-listagem="filtroCategoriaListagem"
        :filtro-email="filtroEmailLocale"
        :filtro-protocolo="filtroProtocoloLocale"
        :filtro-agente-selecionado="filtroAgenteSelecionadoLocale"
        @update:filtro-categoria-listagem="handleFiltroCategoriaListagemChange"
      />
    </v-row>

    <v-expand-transition>
      <v-row
        v-if="mostrarContadoresDeSlas"
        class="ma-0"
      >
        <v-col cols="12">
          <slas-chip-groups
            :fila-id="filaId"
            :slas-contadores="slasContadores"
            @update:sla="handleFiltroSlaChange"
          />
        </v-col>
      </v-row>
    </v-expand-transition>

    <v-row class="justify-space-between mx-0 mt-0">
      <v-col cols="auto">
        <div lass="text-center d-flex align-center justify-space-around">
          <v-tooltip
            top
            color="primary"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="mb-md-0 mb-3 mr-3"
                color="primary"
                :outlined="!filtrosForamAlterados"
                fab
                small
                v-bind="attrs"
                v-on="on"
                @click="limparFiltros"
              >
                <v-icon>
                  {{ icons.mdiFilterRemove }}
                </v-icon>
              </v-btn>
            </template>
            <span>Limpar filtros</span>
          </v-tooltip>

          <v-tooltip
            top
            color="primary"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="mb-md-0 mb-3"
                color="primary"
                fab
                small
                v-bind="attrs"
                v-on="on"
                @click="isFiltrosDialogOpen = !isFiltrosDialogOpen"
              >
                <v-icon>
                  {{ icons.mdiFilter }}
                </v-icon>
              </v-btn>
            </template>
            <span>Filtros</span>
          </v-tooltip>
        </div>
      </v-col>

      <v-col cols="auto">
        <v-tooltip
          v-if="podeCriarTicketManualmente"
          top
          color="primary"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              fab
              small
              v-bind="attrs"
              v-on="on"
              @click="isAddNewTicketDialogOpen = !isAddNewTicketDialogOpen"
            >
              <v-icon>
                {{ icons.mdiPlusThick }}
              </v-icon>
            </v-btn>
          </template>
          <span>Criar ticket</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-divider
      class="mt-2"
      width="100%"
    />

    <perfect-scrollbar
      :options="perfectScrollbarOptions"
      class="h-full mt-2 px-2"
    >
      <ticket-list-item
        v-for="t in tickets"
        :key="t.id"
        :ticket-data="t"
      />
    </perfect-scrollbar>

    <v-pagination
      v-model="paginaAtualLocale"
      :length="numeroDePaginas"
      :total-visible="qtdPageButtonsVisiveis"
      @input="handlePageChange"
    />
  </v-card>
</template>

<script>
import { onMounted, ref, computed, watch } from '@vue/composition-api'
import { mdiFilter, mdiFilterRemove, mdiPlusThick } from '@mdi/js'
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import useAtendimentoStore from '@/store/atendimento'
import CategoriaStatusChipGroups from './CategoriaStatusChipGroups.vue'
import SlasChipGroups from './SlasChipGroups.vue'
import AberturaManualTicketDialog from './AberturaManualTicketDialog.vue'
import TicketFiltrosDialog from './TicketFiltrosDialog.vue'
import TicketListItem from './TicketListItem.vue'
import TiposDeCanaisEnum from '@/models/entities/canal/TiposDeCanaisEnum'
import TicketCategoriaStatusChipEnum from '@/models/entities/ticket/TicketCategoriaStatusChipEnum'
import FilaService from '@/services/modules/FilaService'

export default {
  components: {
    PerfectScrollbar,
    CategoriaStatusChipGroups,
    SlasChipGroups,
    AberturaManualTicketDialog,
    TicketFiltrosDialog,
    TicketListItem,
  },

  emits: [
    'update:filtro-email',
    'update:filtro-categoria-status',
    'update:filtro-protocolo',
    'update:filtro-agente-selecionado',
    'update:pagina-atual',
    'update:filtro-sla-tipo',
    'update:filtro-sla-tipo-alerta',
  ],

  props: {
    filaId: {
      type: Number,
      default: null,
    },
    isGestor: {
      type: Boolean,
      required: true,
    },
    tickets: {
      type: Array,
      required: true,
    },
    slasContadores: {
      type: Object,
      required: true,
    },
    numeroDePaginas: {
      type: Number,
      required: true,
    },
    filtroCategoriaListagem: {
      type: Number,
      default: null,
    },
    getTicketsAndSelectFirst: {
      type: Function,
      required: true,
    },
  },

  setup(props, { emit }) {
    const isAddNewTicketDialogOpen = ref(false)
    const isFiltrosDialogOpen = ref(false)
    const podeCriarTicketManualmente = ref(false)

    const { setTicketSelecionadoId } = useAtendimentoStore()

    const filtroEmailLocale = ref(null)
    const filtroProtocoloLocale = ref(null)
    const filtroAgenteSelecionadoLocale = ref(0)
    const filtroSlaTipoIdLocale = ref(null)
    const filtroSlaTipoAlertaIdLocale = ref(null)
    const filtrosForamAlterados = computed(() => !!filtroEmailLocale.value || !!filtroProtocoloLocale.value || props.filtroCategoriaListagem !== TicketCategoriaStatusChipEnum.Ativos || !!filtroAgenteSelecionadoLocale.value || !!filtroSlaTipoIdLocale.value || !!filtroSlaTipoAlertaIdLocale.value)

    const mostrarContadoresDeSlas = computed(() => [TicketCategoriaStatusChipEnum.Ativos, TicketCategoriaStatusChipEnum.Novos, TicketCategoriaStatusChipEnum.Andamento].includes(props.filtroCategoriaListagem))

    const qtdPageButtonsVisiveis = 8
    const paginaAnterior = ref(1)
    const paginaAtualLocale = ref(1)

    async function determinarSePodeCriarTicketManualmente() {
      if (!props.filaId) return

      const canais = await FilaService.getCanais(props.filaId)

      podeCriarTicketManualmente.value = canais
        .some(it => it.canalTipoId === TiposDeCanaisEnum.AberturaManual && it.ativo === true)
    }

    async function getData() {
      determinarSePodeCriarTicketManualmente()
      props.getTicketsAndSelectFirst()
    }
    onMounted(getData)

    async function handlePageChange(newPage) {
      if (paginaAnterior.value === paginaAtualLocale.value) return

      await new Promise(resolve => {
        paginaAnterior.value = newPage
        resolve()
      })
      await props.getTicketsAndSelectFirst()
    }

    watch(paginaAtualLocale, v => { emit('update:pagina-atual', v) })
    watch(filtroEmailLocale, v => { emit('update:filtro-email', v) })
    watch(filtroProtocoloLocale, v => { emit('update:filtro-protocolo', v) })
    watch(filtroAgenteSelecionadoLocale, v => { emit('update:filtro-agente-selecionado', v) })
    watch(filtroSlaTipoIdLocale, v => { emit('update:filtro-sla-tipo-id', v) })
    watch(filtroSlaTipoAlertaIdLocale, v => { emit('update:filtro-sla-tipo-alerta-id', v) })
    watch(
      () => props.filtroCategoriaListagem,
      v => {
        const statusAtivos = [TicketCategoriaStatusChipEnum.Ativos, TicketCategoriaStatusChipEnum.Novos, TicketCategoriaStatusChipEnum.Andamento]

        if (statusAtivos.includes(v)) {
          return
        }
        filtroSlaTipoIdLocale.value = null
        filtroSlaTipoAlertaIdLocale.value = null
      },
    )

    async function goToPageOne() {
      return new Promise(resolve => {
        paginaAtualLocale.value = 1
        paginaAnterior.value = 1
        resolve()
      })
    }

    async function handleFiltroCategoriaListagemChange(v) {
      if (props.filtroCategoriaListagem === v) return
      emit('update:filtro-categoria-listagem', v)
      await goToPageOne()
      await props.getTicketsAndSelectFirst()
    }

    async function handleFiltroSlaChange(v) {
      filtroSlaTipoIdLocale.value = v.slaTipoId
      filtroSlaTipoAlertaIdLocale.value = v.slaTipoAlertaId
      await goToPageOne()
      await props.getTicketsAndSelectFirst()
    }

    async function filtrarTickets() {
      await goToPageOne()
      await props.getTicketsAndSelectFirst()
    }

    async function resetFilters() {
      return new Promise(resolve => {
        emit('update:filtro-categoria-listagem', TicketCategoriaStatusChipEnum.Ativos)
        filtroEmailLocale.value = null
        filtroProtocoloLocale.value = null
        filtroAgenteSelecionadoLocale.value = 0
        filtroSlaTipoIdLocale.value = null
        filtroSlaTipoAlertaIdLocale.value = null
        resolve()
      })
    }

    watch(
      () => props.filaId,
      async (novaFilaId, filaAnterior) => {
        if (novaFilaId !== filaAnterior) {
          setTicketSelecionadoId(0)

          // Ao trocar de fila, volta pro filtro padrão (Ativos) - sem isso, um filtro deixado em
          // "Finalizados"/"Inativos" numa fila permanecia selecionado ao entrar em outra fila.
          if (filaAnterior !== undefined) {
            await resetFilters()
          }

          await getData()
        }
      },
      { immediate: true },
    )

    async function limparFiltros() {
      if (!filtrosForamAlterados.value) return
      await resetFilters()
      await goToPageOne()
      await props.getTicketsAndSelectFirst()
    }

    const perfectScrollbarOptions = {
      maxScrollbarLength: 100,
      wheelPropagation: true,
    }

    return {
      isAddNewTicketDialogOpen,
      isFiltrosDialogOpen,
      podeCriarTicketManualmente,
      filtroEmailLocale,
      filtroProtocoloLocale,
      filtroAgenteSelecionadoLocale,
      filtroSlaTipoIdLocale,
      filtroSlaTipoAlertaIdLocale,
      filtrosForamAlterados,
      mostrarContadoresDeSlas,
      paginaAtualLocale,
      qtdPageButtonsVisiveis,
      handleFiltroCategoriaListagemChange,
      handleFiltroSlaChange,
      handlePageChange,
      filtrarTickets,
      limparFiltros,
      perfectScrollbarOptions,
      icons: {
        mdiPlusThick,
        mdiFilter,
        mdiFilterRemove,
      },
    }
  },
}
</script>
